import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { addScoreToLeaderboard, loadScores } from "../../utils/leaderboardUtils";
import Button from "../Button";
import {
  PageHeader,
  MessageBox,
  BoxMessage,
  Spacer
} from "../MasterCss";

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4000;
  backdrop-filter: blur(4px);
`;

const LeaderboardModalBox = styled.div`
  background-image: linear-gradient(#21b2d3, #7abefd);
  position: relative;
  width: 90vw;
  max-width: 600px;
  max-height: 85vh;
  overflow-y: auto;
  border-radius: 1em;
  border: 2px solid #08345c;
  filter: drop-shadow(4px 4px 4px #08345c);
  text-align: center;
  padding: 1.5rem 1.5rem 2rem;
  @media (max-width: 480px) {
    padding: 1rem 1rem 1.5rem;
    padding-bottom: 5.4vh;
    padding-top: env(safe-area-inset-top);
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  font-family: Bungee, sans-serif;
  font-size: 2.8vh;
  padding: 1.6vh 2vw;
  margin: 2vh auto;
  width: 70%;
  max-width: 300px;
  border-radius: 1.5vh;
  border: none;
  outline: none;
  color: #333;
  text-align: center;
  display: block;
  background: #ffffff;
  box-shadow: 6px 6px 4px rgba(0, 0, 0, 0.25),
    inset -10px -10px 4px #f0f0f0,
    inset 10px 10px 4px #cccccc;
  &::placeholder {
    color: #888;
    text-transform: uppercase;
    text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.1);
  }
  &:focus {
    box-shadow: 0 0 10px rgba(244, 162, 97, 0.7),
      inset -5px -5px 6px #eee,
      inset 5px 5px 6px #ccc;
  }
  @media (max-width: 480px) {
    font-size: 2.1vh;
    padding: 1vh 2vw;
    width: 80%;
  }
`;

const ErrorText = styled.p`
  color: #ff4d4f;
  font-size: 2vh;
  margin: 0;
  text-align: center;
  min-height: 3vh;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 2vw;
  flex-wrap: wrap;
  margin-top: 1.5vh;
  width: 100%;
  @media (max-width: 480px) {
    gap: 3vw;
  }
`;

const PreviewWrapper = styled.div`
  overflow: hidden;
  max-height: ${({ isOpen }) => (isOpen ? "300px" : "0")};
  opacity: ${({ isOpen }) => (isOpen ? "1" : "0")};
  transition: max-height 0.6s cubic-bezier(0.34, 1.56, 0.64, 1),
              opacity 0.5s ease-in-out;
  margin: 0 auto;
  width: 100%;
`;

const PreviewContainer = styled.div`
  margin: 0.5rem 0;
  padding: 0.8rem;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 1.5em;
  border: 3px solid #f5a623;
  box-shadow: inset 0 0 20px rgba(255, 215, 0, 0.3);
`;

const PreviewTitle = styled.p`
  color: #08345c;
  font-weight: bold;
  font-size: 1.1rem;
  margin: 0 0 0.8rem 0;
  font-family: "Bungee", sans-serif;
  background: rgba(255, 215, 0, 0.3);
  padding: 0.3rem;
  border-radius: 30px;
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const PreviewRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0.8rem;
  font-size: 0.9rem;
  border-radius: 30px;
  margin: 0.2rem 0;
  font-family: "Bungee", sans-serif;
  background: ${({ index }) => {
    if (index === 0) return "rgba(255, 215, 0, 0.4)";
    if (index === 1) return "rgba(192, 192, 192, 0.4)";
    if (index === 2) return "rgba(205, 127, 50, 0.4)";
    return index % 2 === 0 ? "rgba(255, 182, 193, 0.4)" : "rgba(173, 216, 230, 0.4)";
  }};
  border: 1px solid rgba(255, 255, 255, 0.6);
  @media (max-width: 480px) {
    font-size: 0.75rem;
    padding: 0.3rem 0.5rem;
  }
`;

const PreviewRank = styled.span`
  font-weight: bold;
  min-width: 2.5rem;
  font-size: 1.1rem;
  @media (max-width: 480px) {
    font-size: 0.9rem;
    min-width: 2rem;
  }
`;

const PreviewName = styled.span`
  flex: 1;
  text-align: left;
  margin-left: 0.5rem;
  font-weight: 500;
  @media (max-width: 480px) {
    margin-left: 0.2rem;
  }
`;

const PreviewScore = styled.span`
  font-weight: bold;
  font-size: 1.1rem;
  color: #08345c;
  background: rgba(255, 255, 255, 0.5);
  padding: 0.1rem 0.6rem;
  border-radius: 20px;
  @media (max-width: 480px) {
    font-size: 0.9rem;
    padding: 0.1rem 0.4rem;
  }
`;

const NoScoresPreview = styled.p`
  color: #08345c;
  font-size: 1rem;
  padding: 0.8rem;
  font-family: "Bungee", sans-serif;
  background: rgba(255, 215, 0, 0.2);
  border-radius: 30px;
  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 0.5rem;
  }
`;

const TogglePreviewButton = styled.button`
  background: ${({ isOpen }) => (isOpen ? "#ff6b6b" : "#f5a623")};
  border: 2px solid #08345c;
  border-radius: 50px;
  color: #08345c;
  font-size: 1rem;
  font-weight: bold;
  padding: 0.6rem 1.8rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: "Bungee", sans-serif;
  margin: 0.5rem 0;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.25);
  }
  &:active {
    transform: scale(0.95);
  }
  & .arrow {
    display: inline-block;
    transition: transform 0.4s ease;
    transform: ${({ isOpen }) => (isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  }
  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 0.4rem 1.2rem;
    gap: 0.3rem;
  }
`;

const JoinLeaderboardModal = ({ score, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const game = searchParams.get("game");
  const type = searchParams.get("type");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [previewScores, setPreviewScores] = useState([]);

  useEffect(() => {
    if (showPreview) {
      const allScores = loadScores();
      const filtered = allScores
        .filter((s) => s.game === game && s.type === type)
        .sort((a, b) => b.score - a.score)
        .slice(0, 5);
      setPreviewScores(filtered);
    }
  }, [showPreview, game, type]);

  const handleJoin = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError(t("joinLeaderboard.nameRequired"));
      return;
    }
    
    const allScores = loadScores();
    const existing = allScores.some(
      (entry) =>
        entry.game === game &&
        entry.type === type &&
        entry.name?.toLowerCase() === trimmedName.toLowerCase()
    );

    if (existing) {
      setError(t("joinLeaderboard.nameTaken"));
      return;
    }

    setError("");
    addScoreToLeaderboard(game, type, score, trimmedName);
    onClose();
    navigate("/leaderboard");
  };

  const handleSkip = () => {
    onClose();
  };

  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  const getRankEmoji = (index) => {
    if (index === 0) return "🥇";
    if (index === 1) return "🥈";
    if (index === 2) return "🥉";
    return `#${index + 1}`;
  };

  return (
    <Overlay onClick={handleSkip}>
      <LeaderboardModalBox onClick={(e) => e.stopPropagation()}>
        <HeaderWrapper>
          <PageHeader>{t("joinLeaderboard.title")}</PageHeader>
        </HeaderWrapper>

        <Spacer />

        <MessageBox primary>
          <BoxMessage>
            <p>{t("joinLeaderboard.subtitle")}</p>
            <Input
              type="text"
              placeholder={t("joinLeaderboard.namePlaceholder")}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (error) setError("");
              }}
              autoFocus
            />
            <ErrorText>{error}</ErrorText>

            <TogglePreviewButton isOpen={showPreview} onClick={togglePreview}>
              <span>🌟</span>
              {showPreview ? t("joinLeaderboard.hidePreview") : t("joinLeaderboard.preview")}
              <span className="arrow">▼</span>
            </TogglePreviewButton>

            <PreviewWrapper isOpen={showPreview}>
              <PreviewContainer>
                <PreviewTitle>🏆 Top 5 Scores</PreviewTitle>
                {previewScores.length > 0 ? (
                  previewScores.map((entry, index) => (
                    <PreviewRow key={entry.id || index} index={index}>
                      <PreviewRank>{getRankEmoji(index)}</PreviewRank>
                      <PreviewName>{entry.name || "Player"}</PreviewName>
                      <PreviewScore>{entry.score}</PreviewScore>
                    </PreviewRow>
                  ))
                ) : (
                  <NoScoresPreview>🌟 No scores yet. Be the first! 🌟</NoScoresPreview>
                )}
              </PreviewContainer>
            </PreviewWrapper>
          </BoxMessage>
        </MessageBox>

        <Spacer />

        <ButtonRow>
          <Button label={t("joinLeaderboard.skip")} handleClick={handleSkip} />
          <Button primary label={t("joinLeaderboard.join")} handleClick={handleJoin} />
        </ButtonRow>

        <Spacer />
      </LeaderboardModalBox>
    </Overlay>
  );
};

export default JoinLeaderboardModal;